/**
 * -------------------------------------------------
 * Mixins For Auth
 * -------------------------------------------------
 *
 * All form validation must be in "rules" property
 *
 */
import { mapState, mapActions } from 'vuex'

import AuthService from '@/services/auth'
import AuthRequest from '@/api/auth/auth'
import Config from '@/config/app'
import ApiValidator from '@/mixins/api-validator'
import SwitchControl from '@/mixins/switch-control'
import UserRequest from '@/api/auth/user'
import Redirect from '@/mixins/redirect'
import SocialRequest from '@/api/auth/social'
import logger from '@/services/logger'
import utmMappings from '@/utils/utm-mappings'
import AccessRouteRequest from '@/api/common/access-routes'

export default {
  mixins: [ApiValidator, SwitchControl, Redirect],
  data() {
    return {
      redirectRegistrationPage: '/profile-registration',
      redirectChangePasswordPage: '/initial-password-change',
      redirectPage: '/',
      redirectWelcomePage: '/welcome',
      loading: false,
      utmMappings: utmMappings
    }
  },
  computed: {
    ...mapState('applicant', { applicant: 'auth' }),
    /**
     * Is not authenticated user
     *
     * @return bool
     */
    guest() {
      return !AuthService.isAuthenticated()
    },
    /**
     * Is Authenticated User
     *
     * @return bool
     */
    authenticated() {
      return AuthService.isAuthenticated()
    },
    /**
     * Is not authenticated user and not on sign-up route
     *
     * @return bool
     */
    showSignUp() {
      return !AuthService.isAuthenticated() && this.$route.path !== '/sign-up'
    },
    /**
     * Auth User
     *
     * @return {*}
     */
    user() {
      return AuthService.getUser()
    }
  },
  methods: {
    ...mapActions('applicant', { getApplicantRecords: 'getAuth' }),
    ...mapActions('core', ['setLanguage']),
    /**
     * Logout the user
     *
     * @return void
     */
    logout() {
      this.enable('loading')
      AuthRequest.logout().finally(() => {
        AuthService.requestedPage = '/'
        AuthService.clearPersistedRequestedPage()
        AuthService.flush()
        window.location.replace(`${window.location.origin}/login`)
      })
    },
    /**
     * Action Login
     *
     * @return void
     */
    login() {
      this.enable('loading')

      AuthRequest.login({
        email: this.email,
        password: this.password,
        type: Config.CS_TYPE,
        remember_me: this.rememberMe
      })
        .then(({ data }) => this.onSuccess(data))
        .catch(({ response }) => this.onFailed(response))
        .finally(() => this.disable('loading'))
    },
    /**
     * Action Get Auth for Social
     *
     * @return void
     */
    async googleAuth() {
      let { code, state } = this.$route.query
      if (!code) return

      this.enable('loadingGoogle')
      state = JSON.parse(state)
      let param = this.$route.query

      param.type = state.type
      param.token = this.googleToken
      if (this.$route.query.invitation_token !== undefined)
        param.invitation_token = this.$route.query.invitation_token

      // setting access route from cookie
      param = await this.getAccessRoute(param)
      param.first_access_url = this.$cookies.get('firstAccessUrl') || null
      param.last_access_url = this.$cookies.get('referer') || null

      await SocialRequest.authGoogle(param)
        .then(async ({ data }) => {
          // flush cookies of access_route
          this.flushAccessRoute()
          await this.onSuccess(data)
        })
        .catch(({ response }) => {
          this.onFailed(response)
          this.googleErrorResponse()
        })
        .finally(() => this.disable('loadingGoogle'))
    },

    /**
     * Action Authentication for Facebook Social
     *
     * @return void
     */
    async facebookAuth() {
      this.enable('loadingFacebook')
      var param = { type: Config.CS_TYPE }
      if (this.$route.query.invitation_token !== undefined)
        param.invitation_token = this.$route.query.invitation_token

      // setting access route from cookie
      param = await this.getAccessRoute(param)
      param.first_access_url = this.$cookies.get('firstAccessUrl') || null
      param.last_access_url = this.$cookies.get('referer') || null
      await this.facebookPopup(param)
    },

    /**
     * Action Authentication for LinkedIn Social
     *
     * @return void
     */
    async linkedinAuth() {
      this.enable('loadingLinkedin')
      var param = {
        token: this.linkedinToken,
        type: Config.CS_TYPE
      }
      if (this.$route.query.invitation_token !== undefined)
        param.invitation_token = this.$route.query.invitation_token

      // setting access route from cookie
      param = await this.getAccessRoute(param)
      param.first_access_url = this.$cookies.get('firstAccessUrl') || null
      param.last_access_url = this.$cookies.get('referer') || null

      await SocialRequest.authLinkedin(param)
        .then(async ({ data }) => {
          // flush cookies of access_route
          this.flushAccessRoute()

          await this.onSuccess(data)
        })
        .catch(({ response }) => this.onFailed(response))
        .finally(() => this.disable('loadingLinkedin'))
    },

    /**
     * Action Authentication for Github Social
     *
     * @return void
     */
    async githubAuth() {
      this.enable('loadingGithub')
      var param = {
        token: this.githubToken,
        type: Config.CS_TYPE
      }
      if (this.$route.query.invitation_token !== undefined)
        param.invitation_token = this.$route.query.invitation_token

      // setting access route from cookie
      param = await this.getAccessRoute(param)
      param.first_access_url = this.$cookies.get('firstAccessUrl') || null
      param.last_access_url = this.$cookies.get('referer') || null

      await SocialRequest.authGithub(param)
        .then(async ({ data }) => {
          // flush cookies of access_route
          this.flushAccessRoute()

          await this.onSuccess(data)
        })
        .catch(({ response }) => this.onFailed(response))
        .finally(() => this.disable('loadingGithub'))
    },

    /**
     * Setup user on success response
     *
     * @param {*} auth
     * @return void
     */
    async onSuccess(auth) {
      AuthService.setAuth(auth)
      let user = null
      try {
        const resp = await UserRequest.auth()
        user = resp.data
      } catch (error) {
        logger.error(error)
      }

      if (user) {
        AuthService.setUser(user)

        const applicant = await this.getApplicantRecords().then((data) => data)

        if (applicant) {
          AuthService.setRegistrationStatus(applicant.current_step)
          AuthService.setApplicantIdToCookie(applicant.id)

          if (applicant.current_step === 4) {
            const targetPage = AuthService.getRequestedPage()
            AuthService.clearPersistedRequestedPage()
            await this.delay(1000, () =>
              window.location.replace(`${window.location.origin + targetPage}`)
            )
          } else {
            await this.delay(1000, () =>
              window.location.replace(
                `${window.location.origin + this.redirectRegistrationPage}`
              )
            )
          }
          // Delayed so that we can redirect first then apply any changes to the locale
          setTimeout(() => this.setLanguage(applicant.locale), 100)
        }
      }
    },

    /**
     * Handle setTimeout in Promise
     *
     * @param {*} response
     * @return void
     */
    delay(time, action) {
      return new Promise((resolve) => {
        setTimeout(action, time)
      })
    },

    /**
     * Handle failed response
     *
     * @param {*} response
     * @return void
     */
    onFailed(response) {
      if (response.status === 422) this.handleApiErrors(response.data.errors)
    },

    /**
     * setting access route
     *
     * @param {*} params
     * @returns
     */
    async getAccessRoute (params) {
      let accessRoute = null

      if (this.$cookies.get('utmSource') && this.$cookies.get('utmMedium')) {
        const searchParams = {
          source: this.$cookies.get('utmSource'),
          medium: this.$cookies.get('utmMedium'),
          campaign: this.$cookies.get('utmCampaign')
        }

        const term = this.$cookies.get('utmTerm')
        const content = this.$cookies.get('utmContent')

        if (term) {
          searchParams.term = term
        }
        if (content) {
          searchParams.content = content
        }

        accessRoute = await this.fetchRedshiftValue(searchParams);
      } else {
        let referer = this.$cookies.get('referer') || ''

        if (referer !== '') {
          switch (true) {
            case referer.includes('google'):
              accessRoute = 'Google_Organic Search__'
              break
            case referer.includes('t.co'):
              accessRoute = 'X_Organic Social__'
              break
            case referer.includes('linkedin'):
              accessRoute = 'LinkedIn_Organic Social__'
              break
            case referer.includes('instagram'):
              accessRoute = 'Instagram_Organic Social__'
              break
            case referer.includes('facebook'):
              accessRoute = 'Facebook_Organic Social__'
              break

            default:
              accessRoute = null
              break
          }
        }
      }

      params.access_route = accessRoute
      return params
    },

    /**
     * fetch the redshift_value in access_routes table in common API
     * @param searchParams
     * @returns {Promise<null|*>}
     */
    async fetchRedshiftValue(searchParams) {
      try {
        const response = await AccessRouteRequest.searchByField(searchParams)

        // Return the first item or null if empty
        return (response.data.length === 0) ? null : response.data[0];
      } catch (error) {
        return null
      }
    },

    /**
     * flush cookie of access route
     */
    flushAccessRoute() {
      this.$cookies.remove('utmSource')
      this.$cookies.remove('utmMedium')
      this.$cookies.remove('utmCampaign')
      this.$cookies.remove('utmTerm')
      this.$cookies.remove('utmContent')
      this.$cookies.remove('referer')
      this.$cookies.remove('firstAccessUrl')
    }
  }
}
